<!--
  ZoneTable part — the <thead>: the master checkbox, the optional row of group banners and the label
  row. Each column's <th> (label, tooltip, sort control) is HeadCell.vue, in whichever row it lands.
  INTERNAL: not a Zone component, no story, not in llms.txt. Rendered only by Table.vue.

  Presentational. The class rules it renders with come from `./cells`, shared with Body.vue so a
  <th> and the <td> under it can never disagree about width, alignment or sticky layer.

  It forwards the `#header-KEY` slots it is given straight through, which is why Table.vue passes
  `$slots` down: a dynamic slot name cannot be re-declared, only relayed.
-->

<template>
  <thead :class="theadClasses">
    <!--
      `bg-gray-50` on the ROW, not just on each <th>. The selection cell inherits its
      background (it has to, so a body row's hover/expanded tint reaches it), and an
      unpainted <tr> made `background-color: inherit` resolve to TRANSPARENT — which is why
      columns scrolling past were visible straight through the master-checkbox cell.
    -->
    <!-- The group tier. Ungrouped columns render here with `rowspan="2"` and are skipped below. -->
    <tr
      v-if="hasGroups"
      class="bg-gray-50"
      data-testid="column-groups"
    >
      <th
        v-if="selectable"
        scope="col"
        rowspan="2"
        :class="selectionClasses"
      >
        <ZoneCheckbox
          data-testid="select-all"
          :model-value="allSelected"
          :indeterminate="someSelected"
          :disabled="selectAllDisabled"
          @update:model-value="$emit('toggle-all', $event)"
        />
      </th>
      <template
        v-for="span in headerSpans"
        :key="span.id"
      >
        <th
          v-if="span.group"
          scope="colgroup"
          :colspan="span.columns.length"
          class="border-b border-r border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-600 last:border-r-0"
          :data-testid="`column-group-${span.group.key}`"
        >
          <div class="flex min-w-0 items-center gap-1">
            <span class="min-w-0 truncate">{{ span.group.label }}</span>
            <ZoneIcon
              v-if="span.group.tooltip"
              icon="info"
              :tooltip="span.group.tooltip"
              class="shrink-0 text-gray-600"
            />
          </div>
        </th>
        <ZoneTableHeadCell
          v-else
          rowspan="2"
          :column="span.columns[0]"
          :sort="sort"
          :class="headerCellClasses(span.columns[0], span.startIndex, stickyOptions)"
          @toggle-sort="$emit('toggle-sort', $event)"
        >
          <slot
            :name="`header-${span.columns[0].key}`"
            :column="span.columns[0]"
            :sort="sort"
          />
        </ZoneTableHeadCell>
      </template>
    </tr>

    <tr class="bg-gray-50">
      <th
        v-if="selectable && !hasGroups"
        scope="col"
        :class="selectionClasses"
      >
        <ZoneCheckbox
          data-testid="select-all"
          :model-value="allSelected"
          :indeterminate="someSelected"
          :disabled="selectAllDisabled"
          @update:model-value="$emit('toggle-all', $event)"
        />
      </th>
      <ZoneTableHeadCell
        v-for="{ column, classes } in labelRowCells"
        :key="column.key"
        :column="column"
        :sort="sort"
        :class="classes"
        @toggle-sort="$emit('toggle-sort', $event)"
      >
        <slot
          :name="`header-${column.key}`"
          :column="column"
          :sort="sort"
        />
      </ZoneTableHeadCell>
    </tr>
  </thead>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import ZoneIcon from 'zone/components/base/Icon.vue';
import ZoneCheckbox from 'zone/components/base/Checkbox.vue';
import ZoneTableHeadCell from 'zone/components/base/table/HeadCell.vue';
import { headerCellClasses, selectionCellClasses, type StickyOptions } from 'zone/components/base/table/cells';
import type { TableColumn, TableColumnGroup, TableSort } from 'zone/components/base/table/types';

defineOptions({ name: 'ZoneTableHead' });

const props = defineProps({
  columns: {
    type: Array as PropType<TableColumn[]>,
    required: true,
  },
  /** Banners over runs of adjacent same-`group` columns. Empty means one header row, as before. */
  columnGroups: {
    type: Array as PropType<TableColumnGroup[]>,
    default: () => [],
  },
  sort: {
    type: Object as PropType<TableSort | null>,
    default: null,
  },
  selectable: {
    type: Boolean,
    required: true,
  },
  /** Tri-state master checkbox: checked / indeterminate / neither. */
  allSelected: {
    type: Boolean,
    required: true,
  },
  someSelected: {
    type: Boolean,
    required: true,
  },
  selectAllDisabled: {
    type: Boolean,
    required: true,
  },
  stickyHeader: {
    type: Boolean,
    required: true,
  },
  stickyOptions: {
    type: Object as PropType<StickyOptions>,
    required: true,
  },
});

defineEmits(['toggle-all', 'toggle-sort']);

// z-30, not z-20: the body's pinned checkbox cell is itself z-20, and a sticky header that
// ties with it loses on DOM order — the body would scroll over the header.
/**
 * The first header row as runs of ADJACENT columns: a run sharing a `group` key becomes one banner;
 * every other column a full-height cell. `startIndex` indexes the original array, which the
 * pinned-column geometry keys off.
 */
interface HeaderSpan {
  id: string;
  group: TableColumnGroup | null;
  columns: TableColumn[];
  startIndex: number;
}

const groupsByKey = computed<Map<string, TableColumnGroup>>(
  () => new Map(props.columnGroups.map((group) => [group.key, group])),
);

const headerSpans = computed<HeaderSpan[]>(() => props.columns.reduce<HeaderSpan[]>((spans, column, index) => {
  const group = column.group ? groupsByKey.value.get(column.group) ?? null : null;
  const previous = spans.at(-1);

  if (group && previous?.group === group) {
    previous.columns.push(column);

    return spans;
  }

  return [...spans, { id: `${group?.key ?? 'col'}-${index}`, group, columns: [column], startIndex: index }];
}, []));

// An undeclared `group` key earns no tier.
const hasGroups = computed<boolean>(() => headerSpans.value.some((span) => span.group !== null));

/**
 * Without a group tier this is every column. With one it is only the GROUPED columns, because the
 * ungrouped ones already rendered above with `rowspan="2"` — and `last:` sees only this row, so its
 * last cell is told explicitly whether it ends the table (an ungrouped column may follow).
 */
const labelRowCells = computed<{ column: TableColumn; classes: string }[]>(() => {
  if (!hasGroups.value) {
    return props.columns.map((column, index) => ({
      column,
      classes: headerCellClasses(column, index, props.stickyOptions),
    }));
  }

  return headerSpans.value
    .filter((span) => span.group)
    .flatMap((span) => span.columns.map((column, offset) => {
      const index = span.startIndex + offset;

      return {
        column,
        classes: headerCellClasses(column, index, props.stickyOptions, { endsTable: index === props.columns.length - 1 }),
      };
    }));
});

const theadClasses = computed<string>(() => (props.stickyHeader ? 'sticky top-0 z-30' : ''));

const selectionClasses = computed<string>(() => selectionCellClasses(props.stickyOptions, 'header'));
</script>

